import { useEffect, useState } from 'react';
import styled from 'styled-components';

import { SwitchOnProvider } from './SwitchOnProvider/SwitchOnProvider';
import { Introduction } from './Panels/Introduction/Introduction';
import { ApplicationComplete } from './ApplicationComplete/ApplicationComplete';
import { Loading } from './Loading/Loading';
import { LoadError } from './LoadError/LoadError';

import { useGetCarFinanceInitialFormData } from '../queries/hooks/useGetCarFinanceInitialFormData';

import {
  QueryParameters,
  ProviderDisplayName,
} from '../queries/schema/carFinance.schema';

import { media } from '../../../helpers/breakpoints';
import { pushToDataLayer } from '../../../services/gtmService';
import { onSubmitTrackingEvent } from '../financeProviders/shared/tracking/tracking';
import { setStartTime } from '../../../helpers/browserStorageTimer';
import { FINANCE_APPLICATION_START_TIME } from '../constants';
import { RESULT } from '../financeProviders/shared/tracking/tracking.typed';
import { PanelProvider } from '../financeProviders/shared/PanelContext';
import { Panel } from './Panels/shared/Panel/Panel';
import { Space } from 'toolkit';

const Wrapper = styled.div`
  padding: ${({ theme }) => `${theme.spacing.S8} ${theme.spacing.M16}`};
  max-width: 880px;
  width: 100%;
  margin: 0 auto;
  min-height: 720px;

  ${media.medium} {
    padding: ${({ theme }) => `${theme.spacing.M24} 0`};
  }
`;

type CarFinancePageProps = { query: QueryParameters };

const CarFinancePage = ({ query }: CarFinancePageProps) => {
  const { adId, make, model, price, provider } = query;

  const {
    isPending: initialFormDataPending,
    data: initialFormData,
    isError: initialFormDataError,
  } = useGetCarFinanceInitialFormData(query);

  const [isSubmissionSuccessful, setIsSubmissionSuccessful] = useState(false);

  const updateToSuccessState = () => {
    onSubmitTrackingEvent({ result: RESULT.SUCCESS, provider });
    setIsSubmissionSuccessful(true);
  };

  const onSubmitErrorCallback = (errorFields: Array<string>) => {
    onSubmitTrackingEvent({
      result: RESULT.FAILURE,
      provider,
      errorFields,
    });
  };

  useEffect(() => {
    if (
      !localStorage.getItem(FINANCE_APPLICATION_START_TIME) &&
      !sessionStorage.getItem(FINANCE_APPLICATION_START_TIME)
    ) {
      setStartTime(
        FINANCE_APPLICATION_START_TIME,
        new Date().getTime().toString(),
      );
    }
  }, []);

  useEffect(() => {
    pushToDataLayer({
      event: 'car_finance_form_loaded',
      financeProvider: ProviderDisplayName[provider],
    });
  }, [provider]);

  return (
    <Wrapper>
      <Panel as="div">
        {!isSubmissionSuccessful && (
          <>
            {!initialFormDataPending && initialFormData && (
              <>
                <Introduction provider={provider} />
                <Space space="M24" />
                <PanelProvider>
                  <SwitchOnProvider
                    query={query}
                    onSubmitCallback={updateToSuccessState}
                    onSubmitErrorCallback={onSubmitErrorCallback}
                    initialFormData={initialFormData}
                    adId={adId}
                    make={make}
                    model={model}
                    provider={provider}
                    price={price}
                  />
                </PanelProvider>
              </>
            )}
            {!initialFormDataPending && initialFormDataError && <LoadError />}
            {initialFormDataPending && <Loading />}
          </>
        )}
        {isSubmissionSuccessful && <ApplicationComplete />}
      </Panel>
    </Wrapper>
  );
};

export { CarFinancePage };
